import { apiCall, isBackendConfigured } from './api-call'
import { isDevAuthBypassEnabled } from './dev-auth-bypass'
import { readNotifications, writeNotifications } from './local-store'
import { QuickPassNotification } from '../_types/quickpass-notification'
import { buildSeedNotifications } from './notification-seed-data'

const warnFallback = (operation: string, error: unknown): void => {
  console.warn(`[notifications] backend ${operation} failed —`, error)
}

/**
 * Reads notifications from local storage. In dev-bypass mode, seeds a
 * representative list on first read so the UI has something to render.
 * Real Firebase Auth users only ever see what's actually in storage —
 * never seed data.
 *
 * @param {string} quickPassUserId - the QuickPass user whose notifications to read
 * @returns {QuickPassNotification[]} the locally stored notifications, newest first
 */
const readLocalNotifications = (
  quickPassUserId: string,
): QuickPassNotification[] => {
  const existing = readNotifications(quickPassUserId)

  if (existing.length > 0) {
    return existing
  }

  if (isDevAuthBypassEnabled() === false) {
    return []
  }

  const seed = buildSeedNotifications(quickPassUserId)
  writeNotifications(quickPassUserId, seed)
  return seed
}

/**
 * Returns notifications for the renter, newest first. In dev-bypass mode
 * reads from the seeded local store; otherwise hits the backend and
 * surfaces an empty list on failure (never fakes data for real users).
 *
 * @param {string} quickPassUserId - the QuickPass user whose notifications to load
 * @returns {Promise<QuickPassNotification[]>} the notification list
 */
const getNotificationsForUser = async (
  quickPassUserId: string,
): Promise<QuickPassNotification[]> => {
  if (isBackendConfigured() === false) {
    return readLocalNotifications(quickPassUserId)
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getnotifications',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassNotification[]
  } catch (error) {
    warnFallback('getNotifications', error)
    return []
  }
}

/**
 * Marks a single notification read. Updates the backend (when configured)
 * AND the local cache so the bell badge reflects state change immediately.
 *
 * @param {string} quickPassUserId - the QuickPass user the notification belongs to
 * @param {string} notificationId - the notification id to flip
 * @returns {Promise<QuickPassNotification | null>} the updated notification, or null when missing
 */
const markNotificationRead = async (
  quickPassUserId: string,
  notificationId: string,
): Promise<QuickPassNotification | null> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'marknotificationread',
        data: { quickPassUserId, notificationId },
      })

      if (response.code !== 200 && response.code !== 404) {
        throw new Error(response.message)
      }
    } catch (error) {
      warnFallback('markNotificationRead', error)
    }
  }

  const existing = readNotifications(quickPassUserId)
  const now = Date.now()
  let updated: QuickPassNotification | null = null

  const next = existing.map(entry => {
    if (entry.id !== notificationId) {
      return entry
    }

    if (entry.isRead === true) {
      updated = entry
      return entry
    }

    const flipped: QuickPassNotification = {
      ...entry,
      isRead: true,
      readAt: now,
    }
    updated = flipped
    return flipped
  })

  writeNotifications(quickPassUserId, next)
  return updated
}

/**
 * Bulk-flips every unread notification to read for a renter. Returns the
 * count flipped so the caller can show feedback.
 *
 * @param {string} quickPassUserId - the QuickPass user whose unread notifications should be flipped
 * @returns {Promise<number>} the count of notifications marked read
 */
const markAllNotificationsRead = async (
  quickPassUserId: string,
): Promise<number> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'markallnotificationsread',
        data: { quickPassUserId },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }
    } catch (error) {
      warnFallback('markAllNotificationsRead', error)
    }
  }

  const existing = readNotifications(quickPassUserId)
  const now = Date.now()
  let flippedCount = 0

  const next = existing.map(entry => {
    if (entry.isRead === true) {
      return entry
    }

    flippedCount += 1
    return { ...entry, isRead: true, readAt: now }
  })

  writeNotifications(quickPassUserId, next)
  return flippedCount
}

export {
  getNotificationsForUser,
  markNotificationRead,
  markAllNotificationsRead,
}
